import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"
import "./index.scss"

import { Title, Text, Select } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import { limsSstSpec, limsSstResult, limsSstTemplates, message } from "state_management"
import { computeRsd, judgeSst, buildSstRecord } from "state_management/lims/limsSstResult"
import { selectByInstrumentType, selectTemplate } from "state_management/lims/limsSstTemplates"
import useT from "utils/hooks/useT"

// SstPanel (PRD-005 Area 3 / SPEC-017; F4 / SPEC-036) — the per-run System Suitability Test on a
// PLANNED analysis task. Shown ABOVE ResultEntryPanel when the analysis definition requires an SST
// (selectRequiresSst). The analyst enters the (typically six) replicate injections' AREA and
// RETENTION TIME; the calculation engine derives RSD-area and RSD-RT LIVE (via calcEngine.rsd,
// reused through limsSstResult.computeRsd). Tailing factor, plate count (N) and resolution (Rs) are
// ENTERED from the instrument report and judged against the SST criteria. Each parameter shows
// Passed/Failed, the overall SST status is derived, and the analyst e-signs the SST (their own
// sign-off).
//
// F4 (SPEC-036): when the definition tags an instrumentType (HPLC/GC), the analyst may pick a
// SHARED SST TEMPLATE from the library (filtered to that instrument type) — the picked template's
// criteria then drive the judgement (and the picked id is stored on the result). With no template
// the definition's INLINE criteria apply (the back-compatible fallback). The SST is also a 2-step
// signature object: after the analyst's technical sign, a SUPERVISOR signs it off (cap supervisor,
// four-eyes ≠ analyst); the send-to-review gate needs BOTH when the definition requires an SST.
//
// With showLims off this never mounts (the parent gates on showLims + selectRequiresSst). The
// component owns only the editable draft; the persisted/signed record lives in redux.
export default function SstPanel({ analysisId, analysisDefinitionId }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const spec = useSelector(
    (state) => (analysisDefinitionId ? limsSstSpec.selectSstSpec(state, analysisDefinitionId) : undefined),
    shallowEqual
  )
  const saved = useSelector((state) => limsSstResult.selectSstResult(state, analysisId), shallowEqual)

  // F4 — the live shared templates for this analysis's instrument type (empty when the definition
  // has no instrumentType, so the picker stays hidden and the inline criteria apply).
  const instrumentType = (spec && spec.instrumentType) || null
  const templates = useSelector((state) => selectByInstrumentType(state, instrumentType), shallowEqual)

  const injectionCount = (spec && spec.injectionCount) || 6
  const signed = !!(saved && saved.signature)
  const supervisorSigned = !!(saved && saved.supervisorSignature)
  // F4 — the supervisor step applies whenever the definition requires an SST. The SST is fully
  // signed only once BOTH the analyst and the supervisor have signed; the panel drives off the
  // two individual flags below (the send-to-review gate enforces "both" via isSstPassedAndSigned).
  const requiresSupervisor = !!(spec && spec.requiresSst)

  // Editable draft: the injection rows + the three entered instrument parameters + the chosen
  // template. Seeded from the saved record when present, else from a blank set of `injectionCount`
  // rows and the definition's default template.
  const [injections, setInjections] = useState([])
  const [tailing, setTailing] = useState("")
  const [plateCount, setPlateCount] = useState("")
  const [resolution, setResolution] = useState("")
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [showSign, setShowSign] = useState(false)
  const [showSupervisorSign, setShowSupervisorSign] = useState(false)
  const [saving, setSaving] = useState(false)
  const [seeded, setSeeded] = useState(false)

  // F4 — load the shared template library once the panel mounts (cheap, team-scoped; the slice
  // dedupes). Needed to populate the picker + resolve the effective criteria.
  useEffect(() => {
    dispatch(limsSstTemplates.fetch())
  }, [dispatch])

  // Seed the draft ONCE from the saved record (or blank rows). Guarded so live edits aren't clobbered
  // by later store updates (e.g. our own save round-trip).
  useEffect(() => {
    if (seeded) return
    if (saved) {
      setInjections(
        (saved.injections || []).map((inj) => ({
          area: inj.area === null || inj.area === undefined ? "" : String(inj.area),
          rt: inj.rt === null || inj.rt === undefined ? "" : String(inj.rt),
        }))
      )
      setTailing(saved.tailing === null || saved.tailing === undefined ? "" : String(saved.tailing))
      setPlateCount(saved.plateCount === null || saved.plateCount === undefined ? "" : String(saved.plateCount))
      setResolution(saved.resolution === null || saved.resolution === undefined ? "" : String(saved.resolution))
      setSelectedTemplateId(saved.sstTemplateId || null)
      setSeeded(true)
    } else if (injections.length === 0) {
      setInjections(Array.from({ length: injectionCount }, () => ({ area: "", rt: "" })))
      // Pre-select the definition's default template (if any) for a fresh SST.
      if (spec && spec.defaultSstTemplateId) setSelectedTemplateId(spec.defaultSstTemplateId)
    }
  }, [saved, injectionCount, seeded, injections.length, spec])

  const setInjAt = (idx, key, v) =>
    setInjections((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: v } : row)))

  // F4 — the picked template record (any version, resolved by id), and the EFFECTIVE criteria the
  // judgement uses: the template's criteria when one is picked, else the definition's inline spec.
  // Both shapes share the same field names (rsdAreaMax / tailingMin / … ) so judgeSst is reused.
  const pickedTemplate = useSelector((state) => selectTemplate(state, selectedTemplateId), shallowEqual)
  const effectiveSpec = useMemo(() => {
    if (selectedTemplateId && pickedTemplate) {
      return {
        rsdAreaMax: pickedTemplate.rsdAreaMax,
        rsdRtMax: pickedTemplate.rsdRtMax,
        tailingMin: pickedTemplate.tailingMin,
        tailingMax: pickedTemplate.tailingMax,
        plateCountMin: pickedTemplate.plateCountMin,
        resolutionMin: pickedTemplate.resolutionMin,
      }
    }
    return spec
  }, [selectedTemplateId, pickedTemplate, spec])

  // Live computed RSDs + per-parameter judgement from the current draft (vs the effective criteria).
  const live = useMemo(() => {
    const rsdArea = computeRsd(injections, "area")
    const rsdRt = computeRsd(injections, "rt")
    const record = { computed: { rsdArea, rsdRt }, tailing, plateCount, resolution }
    const { perParam, overall } = judgeSst(record, effectiveSpec)
    return { rsdArea, rsdRt, perParam, overall }
  }, [injections, tailing, plateCount, resolution, effectiveSpec])

  const buildDraftRecord = () =>
    buildSstRecord({
      analysisId,
      analysisDefinitionId,
      entry: { injections, tailing, plateCount, resolution },
      spec: effectiveSpec,
      // F4 — the picked shared template id (server denormalizes its current version on the result).
      selectedSstTemplateId: selectedTemplateId || null,
    })

  // Save the SST draft (server recomputes + judges), then sign it with the analyst's e-signature
  // (password verified server-side; "analyst" capability). Saving first guarantees the signed
  // record carries the latest entered values + judgement. Returns the promise so the e-signature
  // dialog can stay open and surface a backend 403 (wrong password / missing capability) inline.
  const handleSignSubmit = (signature) => {
    setSaving(true)
    return dispatch(limsSstResult.saveSst(buildDraftRecord()))
      .then((savedRec) => dispatch(limsSstResult.signSst(savedRec.id, signature)))
      .then(() => {
        setShowSign(false)
        dispatch(message.success(t("limsSstSignedMsg")))
      })
      .finally(() => setSaving(false))
  }

  // F4 — the supervisor signs the technically-signed SST (cap "supervisor", four-eyes ≠ analyst).
  // No save first (the record is frozen after the technical sign); a 403 stays inline in the dialog.
  const handleSupervisorSignSubmit = (signature) => {
    setSaving(true)
    return dispatch(limsSstResult.signSstSupervisor(analysisId, signature))
      .then(() => {
        setShowSupervisorSign(false)
        dispatch(message.success(t("limsSstSupervisorSignedMsg")))
      })
      .finally(() => setSaving(false))
  }

  // Save the SST without signing (lets the analyst record progress / re-judge). Re-seed guard stays
  // set so the save round-trip doesn't reset the in-progress edits.
  const handleSave = () => {
    setSaving(true)
    setSeeded(true)
    dispatch(limsSstResult.saveSst(buildDraftRecord()))
      .then(() => dispatch(message.success(t("limsSstSaved"))))
      .catch(() => dispatch(message.error(t("limsSstSaveFailed"))))
      .finally(() => setSaving(false))
  }

  const fmt = (v) => (v === null || v === undefined || !Number.isFinite(v) ? t("limsSstNotEntered") : v.toFixed(2))

  return (
    <div className="SstPanel">
      <Title>{t("limsSstPanelTitle")}</Title>

      {/* F4 — shared SST-template picker. Shown only when the definition tags an instrument type
          and at least one live template exists for it. Hidden once signed (the choice is frozen).
          Picking one drives the criteria below; "—" reverts to the definition's inline criteria. */}
      {instrumentType && templates.length > 0 && (
        <div className="SstPanel-template">
          <span className="SstPanel-template-label">{t("limsSstTemplateLabel")}:</span>
          {signed ? (
            <span className="SstPanel-template-frozen">
              {pickedTemplate
                ? `${pickedTemplate.name} (v${pickedTemplate.versionNumber})`
                : t("limsSstTemplateInline")}
            </span>
          ) : (
            <div className="SstPanel-template-select">
              <Select
                isMulti={false}
                includeNone
                noneLabel={t("limsSstTemplateInline")}
                collection={templates.map((tpl) => ({
                  id: tpl.id,
                  name: `${tpl.name} (v${tpl.versionNumber})`,
                }))}
                value={selectedTemplateId || ""}
                onChange={(v) => setSelectedTemplateId((v && v[0]) || null)}
                menuPlacement="bottom"
                disabled={saving}
              />
            </div>
          )}
        </div>
      )}

      <Table responsive className="SstPanel-injections">
        <thead>
          <tr>
            <th>{t("limsSstInjection")}</th>
            <th>{t("limsSstArea")}</th>
            <th>{t("limsSstRt")}</th>
          </tr>
        </thead>
        <tbody>
          {injections.map((row, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td>
                <Text value={row.area} onChange={(v) => setInjAt(idx, "area", v)} disabled={signed || saving} />
              </td>
              <td>
                <Text value={row.rt} onChange={(v) => setInjAt(idx, "rt", v)} disabled={signed || saving} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Table responsive className="SstPanel-params">
        <thead>
          <tr>
            <th>{t("limsSstParameter")}</th>
            <th>{t("limsSstValue")}</th>
            <th>{t("limsSstCriterion")}</th>
            <th>{t("limsSstResult")}</th>
          </tr>
        </thead>
        <tbody>
          <ParamRow
            label={t("limsSstComputedRsdArea")}
            value={fmt(live.rsdArea)}
            criterion={maxCriterion(effectiveSpec && effectiveSpec.rsdAreaMax)}
            pass={live.perParam.rsdArea}
            t={t}
          />
          <ParamRow
            label={t("limsSstComputedRsdRt")}
            value={fmt(live.rsdRt)}
            criterion={maxCriterion(effectiveSpec && effectiveSpec.rsdRtMax)}
            pass={live.perParam.rsdRt}
            t={t}
          />
          <tr>
            <td>{t("limsSstTailing")}</td>
            <td>
              <Text value={tailing} onChange={setTailing} disabled={signed || saving} />
            </td>
            <td>{rangeCriterion(effectiveSpec && effectiveSpec.tailingMin, effectiveSpec && effectiveSpec.tailingMax)}</td>
            <td><ResultFlag pass={live.perParam.tailing} t={t} /></td>
          </tr>
          <tr>
            <td>{t("limsSstPlateCount")}</td>
            <td>
              <Text value={plateCount} onChange={setPlateCount} disabled={signed || saving} />
            </td>
            <td>{minCriterion(effectiveSpec && effectiveSpec.plateCountMin)}</td>
            <td><ResultFlag pass={live.perParam.plateCount} t={t} /></td>
          </tr>
          <tr>
            <td>{t("limsSstResolution")}</td>
            <td>
              <Text value={resolution} onChange={setResolution} disabled={signed || saving} />
            </td>
            <td>{minCriterion(effectiveSpec && effectiveSpec.resolutionMin)}</td>
            <td><ResultFlag pass={live.perParam.resolution} t={t} /></td>
          </tr>
        </tbody>
      </Table>

      <div className="SstPanel-overall">
        <span className="SstPanel-overall-label">{t("limsSstOverall")}:</span>{" "}
        <ResultFlag pass={live.overall === null ? null : live.overall === "PASS"} t={t} />
        {signed && saved.signature && (
          <span className="SstPanel-signed">
            {" "}({t("limsSstSignedBy", { initials: saved.signature.initials || "" })})
          </span>
        )}
        {supervisorSigned && saved.supervisorSignature && (
          <span className="SstPanel-signed SstPanel-signed--supervisor">
            {" "}({t("limsSstSupervisorSignedBy", { initials: saved.supervisorSignature.initials || "" })})
          </span>
        )}
      </div>

      {!signed && (
        <div className="SstPanel-actions">
          <Button variant="light" onClick={handleSave} disabled={saving}>
            {t("save")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (live.overall !== "PASS") {
                dispatch(message.warning(t("limsSstSignBlockedNotPassed")))
                return
              }
              setShowSign(true)
            }}
            disabled={saving}
          >
            {t("limsSstSign")}
          </Button>
        </div>
      )}

      {/* F4 — after the analyst's technical sign, the supervisor signs off (only when the
          definition requires an SST, and not already supervisor-signed). */}
      {signed && requiresSupervisor && !supervisorSigned && (
        <div className="SstPanel-actions SstPanel-actions--supervisor">
          <span className="SstPanel-supervisor-hint">{t("limsSstSupervisorPending")}</span>
          <Button variant="primary" onClick={() => setShowSupervisorSign(true)} disabled={saving}>
            {t("limsSstSupervisorSign")}
          </Button>
        </div>
      )}

      {showSign && (
        <ESignatureModal
          title={t("limsSstPanelTitle")}
          reasons={["reviewed", "approved"]}
          onSign={handleSignSubmit}
          onCancel={() => setShowSign(false)}
        />
      )}

      {showSupervisorSign && (
        <ESignatureModal
          title={t("limsSstSupervisorSign")}
          reasons={["reviewed", "approved"]}
          onSign={handleSupervisorSignSubmit}
          onCancel={() => setShowSupervisorSign(false)}
        />
      )}
    </div>
  )
}

// One read-only judged parameter row (the computed RSDs). Entered parameters render inline above.
function ParamRow({ label, value, criterion, pass, t }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
      <td>{criterion}</td>
      <td><ResultFlag pass={pass} t={t} /></td>
    </tr>
  )
}

// Pass / Fail / "—" (not judged) badge. `pass` is true | false | null.
function ResultFlag({ pass, t }) {
  if (pass === null || pass === undefined) {
    return <span className="SstPanel-flag is-pending">—</span>
  }
  return (
    <span className={"SstPanel-flag " + (pass ? "is-pass" : "is-fail")}>
      {pass ? t("limsSstPassed") : t("limsSstFailed")}
    </span>
  )
}

// Human-readable criterion strings (null bound → "—", i.e. that criterion is not judged).
function bound(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v)
}
function maxCriterion(max) {
  return max === null || max === undefined ? "—" : `≤ ${bound(max)}`
}
function minCriterion(min) {
  return min === null || min === undefined ? "—" : `≥ ${bound(min)}`
}
function rangeCriterion(min, max) {
  const hasMin = min !== null && min !== undefined
  const hasMax = max !== null && max !== undefined
  if (hasMin && hasMax) return `${bound(min)} – ${bound(max)}`
  if (hasMin) return `≥ ${bound(min)}`
  if (hasMax) return `≤ ${bound(max)}`
  return "—"
}
